import useMeetingOrigin from '@/meeting/hooks/useMeetingOrigin'
import useCurrentMember from '@/member/hooks/useCurrentMember'
import { useDisclosure } from '@chakra-ui/react'
import { Task_Status_Enum, TaskFragment } from '@gql'
import { TasksViewTypes } from '@rolebase/shared/model/task'
import React, {
  createContext,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react'
import TaskModal from '../modals/TaskModal'

export interface TasksModuleContextParams {
  view: TasksViewTypes
  // The circle written to: it pre-fills the creation form, and nothing else.
  circleId?: string
  // The circles read from, when the module shows more than one (REU12-D1).
  // Kept apart from circleId on purpose: a meeting step widens what it LISTS
  // without moving where a new project LANDS, which stays the meeting's own
  // circle (REU12-D4).
  circleIds?: string[]
  memberId?: string
  status?: Task_Status_Enum
  onViewChange?: (view: TasksViewTypes) => void
  onCircleChange?: (circleId: string | undefined) => void
  onMemberChange?: (memberId: string | undefined) => void
  onStatusChange?: (status: Task_Status_Enum | undefined) => void
}

export interface TasksModuleContextValue extends TasksModuleContextParams {
  openCreateTask?: (defaults?: Partial<TaskFragment>) => void
}

export const TasksModuleContext = createContext<TasksModuleContextValue>({
  view: TasksViewTypes.Kanban,
})

interface TasksModuleProviderProps extends TasksModuleContextParams {
  children: ReactNode
}

export function TasksModuleProvider({
  children,
  ...params
}: TasksModuleProviderProps) {
  const modal = useDisclosure()
  const currentMember = useCurrentMember()
  // In a meeting, no role and nobody by default (#123), unless the step is
  // filtered on a member.
  const inMeeting = !!useMeetingOrigin()

  const [defaults, setDefaults] = useState<Partial<TaskFragment>>({})

  useEffect(() => {
    setDefaults({
      memberId: params.memberId || (inMeeting ? undefined : currentMember?.id),
      circleId: inMeeting ? undefined : params.circleId,
      status: params.status,
    })
  }, [params.memberId, params.circleId, params.status, inMeeting])

  const value: TasksModuleContextValue = useMemo(
    () => ({
      ...params,
      openCreateTask: (defaults) => {
        if (defaults) {
          setDefaults((d) => ({ ...d, ...defaults }))
        }
        modal.onOpen()
      },
    }),
    [
      params.view,
      params.circleId,
      params.circleIds,
      params.memberId,
      params.status,
      params.onViewChange,
      params.onCircleChange,
      params.onMemberChange,
      params.onStatusChange,
    ]
  )

  return (
    <TasksModuleContext.Provider value={value}>
      {children}

      {modal.isOpen && (
        <TaskModal isOpen defaults={defaults} onClose={modal.onClose} />
      )}
    </TasksModuleContext.Provider>
  )
}
